import React, { useEffect, useMemo, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { companyService, Company } from '../services/companyService';
import {
  timeTrackingService,
  CompanyEmployeeUsage,
  EmployeeUsageRow,
  UsageQuery
} from '../services/timeTrackingService';
import { useIsMobile } from '../hooks/useIsMobile';
import './TeamUsage.css';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super admin',
  CLIENT_ADMIN: 'Client admin',
  AGENT: 'Agent',
  END_USER: 'End user'
};

const toYmd = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatHours = (hours: number) =>
  `${hours.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} h`;

const formatCurrency = (amount: number | null, currency = 'EUR') => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const TeamUsage: React.FC = () => {
  const { user } = useAuth();
  const { isSuperAdmin } = usePermissions();
  const [monthDate, setMonthDate] = useState<Date>(new Date());
  const [customRange, setCustomRange] = useState(false);
  const [range, setRange] = useState<Date[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<string>('');
  const [data, setData] = useState<CompanyEmployeeUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const superAdmin = isSuperAdmin();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!superAdmin) return;
    companyService.getCompanies()
      .then((list) => {
        const clients = (list || []).filter((company) => company.type === 'CLIENT');
        setCompanies(clients);
        if (clients[0]?._id) {
          setCompanyId(clients[0]._id);
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        setError('Could not load companies');
        setLoading(false);
      });
  }, [superAdmin]);

  const query = useMemo<UsageQuery>(() => {
    const next: UsageQuery = {};
    if (customRange && range?.[0] && range?.[1]) {
      next.startDate = toYmd(range[0]);
      next.endDate = toYmd(range[1]);
    } else {
      next.month = monthDate.getMonth() + 1;
      next.year = monthDate.getFullYear();
    }
    if (superAdmin && companyId) {
      next.companyId = companyId;
    }
    return next;
  }, [customRange, range, monthDate, superAdmin, companyId]);

  const canLoad = !superAdmin || Boolean(companyId);

  useEffect(() => {
    if (!canLoad) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const result = await timeTrackingService.getCompanyEmployeeUsage(query);
        if (!cancelled) {
          setData(result);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not load team usage');
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [canLoad, query]);

  const periodLabel = customRange && range?.[0] && range?.[1]
    ? `${range[0].toLocaleDateString('en-GB')} – ${range[1].toLocaleDateString('en-GB')}`
    : monthDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const currency = data?.company.currency || 'EUR';
  const companyName = data?.company.name || user?.company?.name || '';

  const nameBody = (row: EmployeeUsageRow) => (
    <div className="usage-person">
      <span className="usage-person-name">{row.user.firstName} {row.user.lastName}</span>
      <span className="usage-person-email">{row.user.email}</span>
    </div>
  );

  return (
    <div className="team-usage">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <i className="pi pi-chart-line"></i>
          </div>
          <div>
            <h1 className="page-title">Team usage</h1>
            <p className="page-subtitle">
              Tickets opened by each employee and hours that cost {companyName || 'the company'} in {periodLabel}.
            </p>
          </div>
        </div>
        <div className="page-header-actions usage-filters">
          {superAdmin && (
            <Dropdown
              value={companyId}
              options={companies.map((company) => ({ label: company.name, value: company._id }))}
              onChange={(e) => setCompanyId(e.value)}
              placeholder="Select company"
              className="usage-company-select"
              filter
            />
          )}
          {customRange ? (
            <div className="usage-month-picker">
              <i className="pi pi-calendar"></i>
              <Calendar
                value={range}
                onChange={(e) => setRange(e.value as Date[])}
                selectionMode="range"
                readOnlyInput
                dateFormat="dd M yy"
                placeholder="From – to"
                hideOnRangeSelection
              />
            </div>
          ) : (
            <div className="usage-month-picker">
              <i className="pi pi-calendar"></i>
              <Calendar
                value={monthDate}
                onChange={(e) => e.value && setMonthDate(e.value as Date)}
                view="month"
                dateFormat="MM yy"
              />
            </div>
          )}
          <Button
            type="button"
            label={customRange ? 'Use month' : 'Custom dates'}
            icon={customRange ? 'pi pi-calendar' : 'pi pi-calendar-plus'}
            className="p-button-secondary"
            onClick={() => {
              setCustomRange((prev) => !prev);
              if (!customRange) {
                const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
                const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
                setRange([start, end]);
              }
            }}
          />
        </div>
      </div>

      {error && <p className="usage-error">{error}</p>}

      <div className="usage-stat-grid team-usage-totals">
        <div className="usage-stat-card">
          <span className="usage-stat-label">Tickets opened</span>
          <strong>{loading ? '—' : data?.totals.ticketsOpened ?? 0}</strong>
        </div>
        <div className="usage-stat-card">
          <span className="usage-stat-label">Hours</span>
          <strong>{loading ? '—' : formatHours(data?.totals.billableHours ?? 0)}</strong>
          <span className="usage-stat-hint">Time logged on this company’s tickets</span>
        </div>
        <div className="usage-stat-card">
          <span className="usage-stat-label">Company cost</span>
          <strong>{loading ? '—' : formatCurrency(data?.totals.cost ?? null, currency)}</strong>
          <span className="usage-stat-hint">
            {data?.company.hourlyRate
              ? `At ${formatCurrency(data.company.hourlyRate, currency)} / h`
              : 'Set an hourly rate on the company to see cost'}
          </span>
        </div>
      </div>

      {isMobile ? (
        <div className="list-cards">
          {(data?.employees || []).map((row) => (
            <div key={row.user._id} className="list-card">
              <div className="list-card-header">
                <div>
                  <div className="list-card-title">{row.user.firstName} {row.user.lastName}</div>
                  <div className="list-card-sub">{row.user.email}</div>
                </div>
                <span className="usage-role-chip">{ROLE_LABELS[row.user.role] || row.user.role}</span>
              </div>
              <div className="list-card-meta usage-card-stats">
                <span><b>{row.ticketsOpened}</b> tickets</span>
                <span><b>{formatHours(row.billableHours)}</b></span>
                <span><b>{formatCurrency(row.cost, currency)}</b></span>
              </div>
            </div>
          ))}
          {!loading && (data?.employees || []).length === 0 && (
            <div className="mobile-empty-state">
              <i className="pi pi-users" />
              <h3>No employees</h3>
              <p>No employees found for this company.</p>
            </div>
          )}
        </div>
      ) : (
      <div className="usage-table-card">
        <DataTable
          value={data?.employees || []}
          loading={loading}
          emptyMessage="No employees found for this company."
          className="usage-table"
          stripedRows
        >
          <Column header="Employee" body={nameBody} />
          <Column
            header="Role"
            body={(row: EmployeeUsageRow) => ROLE_LABELS[row.user.role] || row.user.role}
            style={{ width: '9rem' }}
          />
          <Column field="ticketsOpened" header="Tickets opened" style={{ width: '9rem' }} />
          <Column
            header="Hours"
            body={(row: EmployeeUsageRow) => formatHours(row.billableHours)}
            style={{ width: '10rem' }}
          />
          <Column
            header="Cost"
            body={(row: EmployeeUsageRow) => formatCurrency(row.cost, currency)}
            style={{ width: '9rem' }}
          />
        </DataTable>
      </div>
      )}
    </div>
  );
};

export default TeamUsage;
